/**
 * User preferences.
 *
 * The shell already carries a theme and a layout `<select>` in its footer. Those are for changing
 * a setting you already understand; this page is for the first time, when the difference between
 * "Dim" and "Dark" is not obvious from a five-character label and nobody is going to experiment
 * with five options to find out.
 *
 * So the same choices appear here as radio groups with their descriptions visible, and the effect
 * is immediate — no Save button. A preference that needs confirming is one you cannot try.
 *
 * # These are stored on this device, not on the account
 *
 * Both are read from `localStorage` before the first paint. A preference fetched from the API
 * cannot be: the request has not returned when the browser paints, so a light-mode user gets a
 * dark flash on every page load. Stating the trade-off on the page itself is better than having
 * somebody discover it when their theme does not follow them to a second machine.
 */

import { useEffect, useState, type JSX } from 'react';

import {
  LAYOUT_OPTIONS,
  resolveLayout,
  setLayoutMode,
  storedLayoutMode,
  type LayoutMode,
} from '../shared/theme/layout';
import {
  THEME_OPTIONS,
  applyTheme,
  currentTheme,
  hasExplicitPreference,
  type Theme,
} from '../shared/theme/theme';
import { detectedAsMobile } from '../shell/LayoutPicker';
import { Card, PageHeader } from '../shared/ui/ui';

export function Preferences(): JSX.Element {
  const [theme, setTheme] = useState<Theme>(() => currentTheme());
  const [layout, setLayout] = useState<LayoutMode>(() => storedLayoutMode());
  const [following, setFollowing] = useState<boolean>(() => !hasExplicitPreference());

  // The shell's pickers write the same storage keys, so a change made there while this page is
  // open would leave these controls showing a stale selection. Re-reading on focus is cheap and
  // covers the case without either component needing to know the other exists.
  useEffect(() => {
    const sync = (): void => {
      setTheme(currentTheme());
      setLayout(storedLayoutMode());
      setFollowing(!hasExplicitPreference());
    };
    window.addEventListener('focus', sync);
    return (): void => {
      window.removeEventListener('focus', sync);
    };
  }, []);

  const chooseTheme = (next: Theme): void => {
    applyTheme(next);
    setTheme(next);
    // Choosing explicitly stops the application following the system at sunset. Switching somebody
    // out of the theme they just picked would be worse than not following at all.
    setFollowing(false);
  };

  const chooseLayout = (next: LayoutMode): void => {
    setLayoutMode(next);
    setLayout(next);
  };

  const effectiveLayout = resolveLayout(layout);
  const mobile = detectedAsMobile();

  return (
    <>
      <PageHeader
        title="Preferences"
        subtitle="Appearance and layout. Both take effect immediately and are stored on this device."
      />

      <Card
        title="Appearance"
        subtitle="Dark for long sessions, light for shared screens and projectors, high contrast where legibility has to be guaranteed."
      >
        <fieldset className="choice">
          <legend className="visually-hidden">Theme</legend>
          {THEME_OPTIONS.map((option) => (
            <label
              key={option.id}
              className={theme === option.id ? 'choice__item choice__item--on' : 'choice__item'}
            >
              <input
                type="radio"
                name="theme"
                value={option.id}
                checked={theme === option.id}
                onChange={() => {
                  chooseTheme(option.id);
                }}
              />
              <span className="choice__body">
                <span className="choice__label">{option.label}</span>
                <span className="choice__description">{option.description}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <p className="form__hint">
          {following ? (
            <>
              Currently <strong>following your system setting</strong> — the application switches
              with your operating system, including its contrast preference. Choosing above stops
              that and fixes the theme.
            </>
          ) : (
            <>
              You have chosen <strong>{labelOf(THEME_OPTIONS, theme)}</strong> explicitly, so the
              application no longer follows your system setting.
            </>
          )}
        </p>
      </Card>

      <Card
        title="Layout"
        subtitle="The default is the full desktop application everywhere except a genuine phone or tablet. Window width is deliberately not consulted."
      >
        <fieldset className="choice">
          <legend className="visually-hidden">Layout</legend>
          {LAYOUT_OPTIONS.map((option) => (
            <label
              key={option.id}
              className={layout === option.id ? 'choice__item choice__item--on' : 'choice__item'}
            >
              <input
                type="radio"
                name="layout"
                value={option.id}
                checked={layout === option.id}
                onChange={() => {
                  chooseLayout(option.id);
                }}
              />
              <span className="choice__body">
                <span className="choice__label">{option.label}</span>
                <span className="choice__description">{option.description}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <p className="form__hint">
          This device is detected as a <strong>{mobile ? 'phone or tablet' : 'desktop'}</strong>, so{' '}
          <em>Automatic</em> resolves to <strong>{effectiveLayout}</strong>. Detection asks what the
          device can do — whether it has a coarse pointer that cannot hover — rather than trusting
          the user agent string, which lies routinely and increasingly by design.
        </p>
        <p className="form__hint">
          Narrowing a window on a PC will <em>not</em> switch you to the compact layout. Somebody
          working in a half-width split came for the full board, and taking columns away because
          they resized is worse than a horizontal scrollbar.
        </p>
      </Card>

      <Card title="Where these are kept">
        <p>
          Both settings live in this browser, on this device — not on your Conduit account. They are
          read before the page paints, which an account setting cannot be: the request has not
          returned by the time the browser draws, so every page load would flash the wrong theme
          first.
        </p>
        <p className="form__hint">
          The consequence is that these choices do not follow you to another browser or another
          machine. That is the deliberate trade: a preference that is right instantly on the device
          in front of you beats one that is right everywhere, half a second late.
        </p>
      </Card>
    </>
  );
}

function labelOf(options: readonly { id: string; label: string }[], id: string): string {
  return options.find((o) => o.id === id)?.label ?? id;
}
